import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Logo, LogoCategory, LogosState, ThunkAction } from "../types/index.js";
import { ModuleNotFoundError } from "../utils/utils.js";
import { debouncedPatchModuleRequest } from "./modules/thunks.js";
import { getPartners } from "./partners.js";

const initialState: LogosState = {};

export const changeLogo =
  (
    siteId: string,
    action:
      | ReturnType<typeof moveLogoStart>
      | ReturnType<typeof upsertLogoStart>
      | ReturnType<typeof deleteLogoStart>,
  ): ThunkAction<void> =>
  (dispatch, getState) => {
    dispatch(action);

    const module = getState().modules.byId[action.payload.moduleId];
    if (!module) throw new ModuleNotFoundError(action.payload.moduleId);

    debouncedPatchModuleRequest({
      dispatch,
      getState,
      siteId,
      moduleId: action.payload.moduleId,
      patch: { settings: module.settings },
    });
  };

const logos = createSlice({
  name: "logos",
  initialState,
  reducers: {
    moveLogoStart: (
      _state,
      _action: PayloadAction<{
        moduleId: string;
        logoCategory: LogoCategory;
        dragIndex: number;
        hoverIndex: number;
      }>,
    ) => {},

    upsertLogoStart: (
      _state,
      _action: PayloadAction<{
        moduleId: string;
        logoCategory: LogoCategory;
        newLogoId: string;
        previousLogoId: string | undefined;
      }>,
    ) => {},

    deleteLogoStart: (
      _state,
      _action: PayloadAction<{
        moduleId: string;
        logoCategory: LogoCategory;
        logoId: string;
      }>,
    ) => {},
  },
  extraReducers: (builder) => {
    builder.addCase(getPartners.fulfilled, (_state, { payload }) =>
      payload.reduce<LogosState>(
        (newState, partner) =>
          partner.logos.reduce<LogosState>((newState, logo) => {
            const newLogo: Logo = { ...logo, partnerId: partner.id };

            return { ...newState, [logo.id]: newLogo };
          }, newState),
        initialState,
      ),
    );
  },
});

export const { moveLogoStart, upsertLogoStart, deleteLogoStart } =
  logos.actions;

export default logos.reducer;
